import React from 'react';
import { Card } from '../ui';
import { ToggleRow } from './ToggleRow';

type RealPhotosCardProps = {
  /** Whether the plan sells the capability at all (Creator+). */
  entitled: boolean;
  enabled: boolean;
  setEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  allowEditing: boolean;
  setAllowEditing: React.Dispatch<React.SetStateAction<boolean>>;
  allowPeople: boolean;
  setAllowPeople: React.Dispatch<React.SetStateAction<boolean>>;
  markDirty: (_field: string) => void;
};

/**
 * Consent for publishing real photographs from the web on the user's account.
 *
 * Deliberately its own card rather than a row under Publishing: this is the one
 * setting where the product acts with someone else's work on the user's behalf,
 * so it states what happens in plain words and stays off until they say yes.
 */
export function RealPhotosCard({
  entitled,
  enabled,
  setEnabled,
  allowEditing,
  setAllowEditing,
  allowPeople,
  setAllowPeople,
  markDirty,
}: RealPhotosCardProps) {
  return (
    <Card elevation="raised">
      <Card.Header>
        <Card.Title subtitle="Let Conistra use real photographs, not only generated images.">
          <span className="settings-section-icon">📷</span> Real Photos
        </Card.Title>
      </Card.Header>
      <Card.Body className="settings-card-body">
        <ToggleRow
          label="Use real photos"
          description={
            entitled
              ? 'For posts about things that actually exist — a real place, a real product — Conistra finds a real photograph instead of generating a lookalike. Only photos licensed for commercial use, and the photographer is credited in your caption automatically.'
              : '🔒 Real photos require the Creator plan or higher.'
          }
          checked={enabled && entitled}
          onChange={(v) => {
            if (!entitled) return;
            setEnabled(v);
            markDirty('realPhotosEnabled');
          }}
          disabled={!entitled}
        />
        {entitled && enabled && (
          <>
            <ToggleRow
              label="Let the image model edit them"
              description="Reframe for Instagram, colour-grade to match your feed, set your headline on the photo, or combine two photos into one slide. Off means photos are posted exactly as licensed. Editing never changes what a photo shows."
              checked={allowEditing}
              onChange={(v) => {
                setAllowEditing(v);
                markDirty('realPhotosAllowEditing');
              }}
            />
            <ToggleRow
              label="Allow photos of identifiable people"
              description="On by default — people are normal subject matter. A licence covers the photographer, not the people in the frame, so turn this off if you would rather keep faces out of sourced photos. Generated pictures can always show people."
              checked={allowPeople}
              onChange={(v) => {
                setAllowPeople(v);
                markDirty('realPhotosAllowPeople');
              }}
            />
            <p className="settings-hint-text">
              Every slide shows where its picture came from, and you can open the licence before
              you approve the post.
            </p>
          </>
        )}
      </Card.Body>
    </Card>
  );
}
